import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bell, CreditCard, UserCircle, Lock, LogOut } from "lucide-react";
import PageTransition from "@/components/PageTransition";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ProfileForm from "@/components/ProfileForm";
import { supabase } from "@/integrations/supabase/client";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface TabProps {
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const Account = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [showModal, setShowModal] = useState(false);
  const [alertDays, setAlertDays] = useState(7);
  const [alertMethods, setAlertMethods] = useState({
    email: true,
    push: false,
    sms: false
  });
  const [alertFrequency, setAlertFrequency] = useState("daily");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { signOut, user } = useAuth();
  
  useEffect(() => {
    if (user) {
      fetchAlertPreferences();
    }
  }, [user]);
  
  const fetchAlertPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('alert_window')
        .eq('id', user?.id)
        .maybeSingle();
        
      if (error) throw error;
      
      if (data) {
        setAlertDays(data.alert_window || 7);
      }
    } catch (error) {
      console.error("Error fetching alert preferences:", error);
    }
  };
  
  const handleSaveAlerts = async () => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          alert_window: alertDays,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
        
      if (error) throw error;
      
      toast.success("Alert preferences updated", {
        position: "bottom-right",
        dismissible: true,
      });
    } catch (error) {
      console.error("Error updating alert preferences:", error);
      toast.error("Failed to update alert preferences", {
        position: "bottom-right",
        dismissible: true,
      });
    }
  };
  
  const handleUpgradeClick = () => {
    setShowModal(true);
  };
  
  const handleSavePassword = async () => {
    // Input validation
    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    
    if (!newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    
    // Password strength validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      toast.error("Password doesn't meet security requirements");
      return;
    }
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
      toast.success("Password updated successfully", {
        position: "bottom-right",
        dismissible: true,
      });
    } catch (error: any) {
      console.error("Error updating password:", error);
      toast.error(error.message || "Failed to update password", {
        position: "bottom-right",
        dismissible: true,
      });
    }
  };
  
  const handleAlertDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAlertDays(parseInt(e.target.value));
  };
  
  const handleAlertMethodChange = (method: keyof typeof alertMethods) => {
    setAlertMethods(prev => ({
      ...prev,
      [method]: !prev[method]
    }));
  };
  
  const handleEnable2FA = () => {
    // Use the sonner toast instead of the shadcn toast
    toast.info("2FA will be available in the Pro plan", {
      position: "bottom-right",
      dismissible: true,
      duration: 5000
    });
  };
  
  const tabs: TabProps[] = [
    {
      label: "Profile",
      icon: <UserCircle className="w-5 h-5" />,
      content: <ProfileForm />,
    },
    {
      label: "Alerts",
      icon: <Bell className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Alert Preferences</h2>
          <p className="text-gray-600">Customize how and when you receive notifications about expiring items.</p>
          
          <div className="space-y-4">
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Alert Window</h3>
                  <p className="text-sm text-gray-600">Days before expiration to receive alerts</p>
                </div>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="1"
                    max="30"
                    value={alertDays}
                    onChange={handleAlertDaysChange}
                    className="w-40 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-expitrack-primary"
                  />
                  <span className="bg-expitrack-primary text-white rounded-lg px-3 py-1 min-w-[3rem] text-center">
                    {alertDays}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="p-4 border border-gray-200 rounded-lg">
              <h3 className="font-medium mb-4">Notification Methods</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input 
                      id="email-alerts" 
                      type="checkbox" 
                      className="h-4 w-4 accent-expitrack-primary rounded"
                      checked={alertMethods.email}
                      onChange={() => handleAlertMethodChange("email")}
                    />
                    <label htmlFor="email-alerts" className="ml-2 text-sm text-gray-700">Email Alerts</label>
                  </div>
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                    All Plans
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input 
                      id="push-alerts" 
                      type="checkbox" 
                      className="h-4 w-4 accent-expitrack-primary rounded"
                      checked={alertMethods.push}
                      onChange={() => handleAlertMethodChange("push")}
                    />
                    <label htmlFor="push-alerts" className="ml-2 text-sm text-gray-700">Push Notifications</label>
                  </div>
                  <span className="text-xs px-2 py-1 bg-expitrack-primary/10 text-expitrack-primary rounded">
                    Pro Plan
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input 
                      id="sms-alerts" 
                      type="checkbox" 
                      className="h-4 w-4 accent-expitrack-primary rounded" 
                      disabled
                      checked={alertMethods.sms}
                      onChange={() => handleAlertMethodChange("sms")}
                    />
                    <label htmlFor="sms-alerts" className="ml-2 text-sm text-gray-400">SMS Alerts</label>
                  </div>
                  <span className="text-xs px-2 py-1 bg-expitrack-primary/10 text-expitrack-primary rounded">
                    Pro Plan
                  </span>
                </div>
              </div>
            </div>
            
            <div className="p-4 border border-gray-200 rounded-lg">
              <h3 className="font-medium mb-4">Alert Frequency</h3>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input 
                    id="freq-daily" 
                    type="radio" 
                    name="frequency"
                    className="h-4 w-4 accent-expitrack-primary" 
                    checked={alertFrequency === "daily"}
                    onChange={() => setAlertFrequency("daily")}
                  />
                  <label htmlFor="freq-daily" className="ml-2 text-sm text-gray-700">Daily Digest</label>
                </div>
                <div className="flex items-center">
                  <input 
                    id="freq-immediate" 
                    type="radio" 
                    name="frequency"
                    className="h-4 w-4 accent-expitrack-primary" 
                    checked={alertFrequency === "immediate"}
                    onChange={() => setAlertFrequency("immediate")}
                  />
                  <label htmlFor="freq-immediate" className="ml-2 text-sm text-gray-700">Immediately</label>
                </div>
                <div className="flex items-center">
                  <input 
                    id="freq-weekly" 
                    type="radio" 
                    name="frequency"
                    className="h-4 w-4 accent-expitrack-primary" 
                    checked={alertFrequency === "weekly"}
                    onChange={() => setAlertFrequency("weekly")}
                  />
                  <label htmlFor="freq-weekly" className="ml-2 text-sm text-gray-700">Weekly Summary</label>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <button 
              onClick={handleSaveAlerts}
              className="px-6 py-2 bg-expitrack-primary text-white rounded-lg font-medium hover:bg-expitrack-primary/90 transition-colors"
            >
              Save Preferences
            </button>
          </div>
        </div>
      ),
    },
    {
      label: "Billing",
      icon: <CreditCard className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Billing & Subscription</h2>
          <p className="text-gray-600">Manage your payment methods and subscription details.</p>
          
          <div className="bg-expitrack-primary/5 p-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Current Plan: Free</h3>
                <p className="text-sm text-gray-600">You're currently on the Free plan with basic features.</p>
              </div>
              <button 
                className="px-4 py-2 bg-expitrack-primary text-white rounded-lg text-sm font-medium hover:bg-expitrack-primary/90 transition-colors"
                onClick={handleUpgradeClick}
              >
                Upgrade to Pro
              </button>
            </div>
          </div>
          
          <div className="p-4 border border-gray-200 rounded-lg">
            <h3 className="font-medium mb-4">Payment Methods</h3>
            <p className="text-sm text-gray-600 mb-4">
              No payment methods have been added yet.
            </p>
            <button 
              className="px-4 py-2 border border-gray-300 bg-white rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              onClick={handleUpgradeClick}
            >
              Add Payment Method
            </button>
          </div>
          
          <div className="p-4 border border-gray-200 rounded-lg">
            <h3 className="font-medium mb-4">Billing History</h3>
            <div className="text-sm text-gray-600 italic">
              No billing history available.
            </div>
          </div>
        </div>
      ),
    },
    {
      label: "Security",
      icon: <Lock className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Security Settings</h2>
          <p className="text-gray-600">Manage your password and security preferences.</p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <input 
                type="password" 
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-expitrack-primary/50 focus:border-expitrack-primary"
                placeholder="Enter your current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input 
                type="password" 
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-expitrack-primary/50 focus:border-expitrack-primary"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <input 
                type="password" 
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-expitrack-primary/50 focus:border-expitrack-primary"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg mt-4 text-sm text-gray-600">
            <h4 className="font-medium text-gray-700 mb-2">Password Requirements:</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>At least 8 characters long</li>
              <li>Include at least one uppercase letter</li>
              <li>Include at least one number</li>
              <li>Include at least one special character</li>
            </ul>
          </div>
          
          <div className="pt-4 border-t border-gray-100">
            <h3 className="text-lg font-semibold mb-4">Two-Factor Authentication</h3>
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h4 className="font-medium">Enhance Your Account Security</h4>
                <p className="text-sm text-gray-600">Add an extra layer of security to your account.</p>
              </div>
              <button 
                className="px-4 py-2 border border-gray-300 bg-white rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                onClick={handleEnable2FA}
              >
                Enable 2FA
              </button>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <button 
              onClick={handleSavePassword}
              className="px-6 py-2 bg-expitrack-primary text-white rounded-lg font-medium hover:bg-expitrack-primary/90 transition-colors"
            >
              Update Password
            </button>
          </div>
        </div>
      ),
    },
  ];
  
  return (
    <PageTransition className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Profile Settings</h1>
          <p className="text-gray-600">
            Manage your profile, notifications, and account preferences.
          </p>
        </div>
        
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full md:w-64 space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.label.toLowerCase()}
                className={`w-full px-4 py-3 rounded-lg flex items-center text-left transition-colors ${
                  activeTab === tab.label.toLowerCase()
                    ? 'bg-expitrack-primary text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setActiveTab(tab.label.toLowerCase())}
              >
                <span className={`mr-3 ${activeTab === tab.label.toLowerCase() ? 'text-white' : 'text-gray-500'}`}>
                  {tab.icon}
                </span>
                {tab.label}
              </button>
            ))}
            
            <button 
              className="w-full px-4 py-3 rounded-lg flex items-center text-left text-gray-700 hover:bg-gray-100 transition-colors mt-4"
              onClick={signOut}
            >
              <LogOut className="w-5 h-5 mr-3 text-gray-500" />
              Logout
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1">
            <motion.div
              className="bg-white rounded-xl p-6 md:p-8 shadow-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              key={activeTab}
            >
              {tabs.find(tab => tab.label.toLowerCase() === activeTab)?.content}
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Pro Plan Upgrade Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade to Pro Plan</DialogTitle>
            <DialogDescription>
              The Pro Plan is coming soon! Get access to premium features when it launches.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <h3 className="font-medium">Pro Plan Features:</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Push notifications for expiring items</li>
              <li>SMS alerts</li>
              <li>Advanced item tracking</li>
              <li>Priority support</li>
              <li>Two-factor authentication</li>
            </ul>
            <p className="text-sm text-gray-600 italic mt-4">
              Pro Plan pricing will be announced soon. We'll notify you when it becomes available.
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setShowModal(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageTransition>
  );
};

export default Account;
